// tslint:disable

import * as groupView from '../../core';
import { inject, injectable, interfaces } from 'inversify';
import {
    IEditorGroupsService, IFindGroupScope, GroupDirection, GroupLocation, GroupsOrder,
    CacheTypenames, IEditorService, ClientTypes, IClientConfigurationService,
    IAddGroupOptions, ITextEditor,
    IResourceSideBySide_Input,
    generateSideBySideEditorInput,
    generateDiffEditorInput,
    IEditorInput,
    IIEditorInput,
    IEditorGroup,
    IEditorOptions,
    GroupIdentifier,
    SIDE_GROUP_TYPE,
    ACTIVE_GROUP_TYPE,
    SIDE_GROUP,
    ITextEditorOptions,
    IUntitledResource_Input,
    IResourceDiff_Input,
    IResourceInput,
    ITextDiffEditor,
    ITextSideBySideEditor,
    IResourceEditor,
    IEditorInputWithOptions,
    ISideBySideEditorInput,
    IDiffEditorInput,
    EDITOR_INPUT_ID,
    preferredSideBySideGroupDirection,
    IEditorInputFactoryRegistry,
    IResourceEditorInput,
    IDataUriEditorInput,
    IIEditorInputWithGroupFields,
    IClientEditor as IEditor,
    IFileStatWithMetadata,
    IEditorPart,
    resourcePath,
    IUntitledEditorInput,
} from '@workbench-stack/core';
import { withNullAsUndefined, withUndefinedAsNull } from '@vscode-alt/monaco-editor/esm/vs/base/common/types';
import { EditorLayoutDefaultID, ClientExtensions } from '../../constants';
import { localize } from '@vscode-alt/monaco-editor/esm/vs/nls';
import { Schemas } from '@vscode-alt/monaco-editor/esm/vs/base/common/network';
import { ClientTypes as CommonTypes } from '@common-stack/client-react';
import { ApolloClient } from 'apollo-client';
import { InMemoryCache } from 'apollo-cache-inmemory';
import { TextEditorOptions, EditorOptions, DEFAULT_GROUP_ID } from '../../core';
import { basename, DataUri } from '@vscode-alt/monaco-editor/esm/vs/base/common/resources';
import { IClientFileService as IFileService, ClientTypes as FilesTypes, IUntitledEditorService } from '@files-stack/core';
import { URI } from '@vscode-alt/monaco-editor/esm/vs/base/common/uri';
import { Registry } from '@vscode-alt/monaco-editor/esm/vs/platform/registry/common/platform';
import * as _ from 'lodash';
import { BINARY_FILE_EDITOR_ID, TEXT_FILE_EDITOR_ID } from '@workbench-stack/core';

@injectable()
export abstract class AbstractEditorService implements IEditorService {


    constructor(
        @inject(ClientTypes.IConfigurationService)
        protected readonly configurationService: IClientConfigurationService,
        @inject(FilesTypes.IFileService)
        protected fileService: IFileService,
        @inject(FilesTypes.IUntitledEditorService)
        protected untitledEditorService: IUntitledEditorService,
        @inject(ClientTypes.IEditorGroupsService)
        protected editorGroupService: IEditorGroupsService,
        @inject('Container')
        protected container: interfaces.Container,
        @inject(CommonTypes.ApolloClient)
        protected client: ApolloClient<any>,
        @inject(CommonTypes.InMemoryCache)
        protected cache: InMemoryCache,
        @inject(CommonTypes.UtilityClass)
        protected utility,
        @inject(CommonTypes.Logger)
        protected logger: any,
    ) {

        

    }

    public abstract getLayout(): IEditorPart

    get editors(): IEditorInput[] {
        const editors: IEditorInput[] = [];
        this.editorGroupService.groups.forEach(group => {
            editors.push(...group.tabs);
        });

        return editors;
    }

    get activeEditor(): IEditorInput | undefined {
        const activeGroup = this.editorGroupService.activeGroup;

        return activeGroup ? withNullAsUndefined(
            activeGroup.tabs.find(t => t.isActive)) : undefined;
    }


    public openEditor(editor: IEditorInput, options?: IEditorOptions | ITextEditorOptions, group?: IEditorGroup | GroupIdentifier | SIDE_GROUP_TYPE | ACTIVE_GROUP_TYPE): Promise<IEditor | null>;
    public openEditor(editor: IResourceInput | IUntitledResource_Input, group?: IEditorGroup | GroupIdentifier | SIDE_GROUP_TYPE | ACTIVE_GROUP_TYPE): Promise<ITextEditor | null>;
    public openEditor(editor: IResourceDiff_Input, group?: IEditorGroup | GroupIdentifier | SIDE_GROUP_TYPE | ACTIVE_GROUP_TYPE): Promise<ITextDiffEditor | null>;
    public openEditor(editor: IResourceSideBySide_Input, group?: IEditorGroup | GroupIdentifier | SIDE_GROUP_TYPE | ACTIVE_GROUP_TYPE): Promise<ITextSideBySideEditor | null>;
    public openEditor(editor: IEditorInput | IResourceEditor, optionsOrGroup?: IEditorOptions | ITextEditorOptions | IEditorGroup | GroupIdentifier | SIDE_GROUP_TYPE | ACTIVE_GROUP_TYPE, group?: GroupIdentifier): Promise<IEditor | null> {

        // Typed Editor Support
        if ((editor as IEditorInput).typeId){
            const editorOptions = this.toOptions(optionsOrGroup as IEditorOptions);
            const targetGroup = this.findTargetGroup(editor as IEditorInput, editorOptions, group);

            return this.doOpenEditor(targetGroup, editor as IEditorInput, editorOptions);
        }

        // Untyped Text Editor Support
        const textInput = <IResourceEditor>editor;
        const typedInput = this.createInput(textInput);
        if (typedInput) {
            /*TextEditorOptions.from(textInput) is not returned options*/
            // const editorOptions = TextEditorOptions.from(textInput);
            const editorOptions = textInput.options;
            const targetGroup = this.findTargetGroup(typedInput, editorOptions, group as IEditorGroup | GroupIdentifier);
            let updatedTypeInput = {};
            if (typedInput.typeId === EDITOR_INPUT_ID.DIFF_EDITOR_INPUT) {
                updatedTypeInput = { ...typedInput, ...this.addIds(targetGroup.id, (<IDiffEditorInput>typedInput).originalInput.resource || (<IDiffEditorInput>typedInput).modifiedInput.resource, typedInput.label, typedInput.__typename) }
            } else if(typedInput.typeId === EDITOR_INPUT_ID.SIDE_BY_SIDE_EDITOR_INPUT) {
                updatedTypeInput = { ...typedInput, ...this.addIds(targetGroup.id, (<ISideBySideEditorInput>typedInput).resource || (<ISideBySideEditorInput>typedInput).master.resource || (<ISideBySideEditorInput>typedInput).details.resource, typedInput.label, typedInput.__typename) }
            } else {
                updatedTypeInput  = { ...typedInput, ...this.addIds(targetGroup.id, typedInput.resource, typedInput.label, typedInput.__typename) }
            }
            return this.doOpenEditor(targetGroup, updatedTypeInput, editorOptions);
        }

        return Promise.resolve(null);
    }

    protected abstract setEditorPart(data);

    protected abstract getEditorPart(layoutId: string);

    protected async doOpenEditor(group: IEditorGroup | undefined, editor: IEditorInput, options?: IEditorOptions): Promise<IEditor | null> {
        if (!options) {
            options = { inactive: false };
        }
        const groups = this.editorGroupService.groups.slice(0);
        const result = groupView.openEditor(editor, {
            editors: group ? group.tabs : [],
            groups: groups,
        }, options);

        this.setTabsWithFiles(group.id, result.editors);
        this.editorGroupService.setGroups(result.groups);

        // when original group is empty, means it is a new group.
        // attach to the `EditorLayout`
        if (groups === undefined || (!groups.length && result.groups.length === 1)) {
            const targetGroup = result.groups[0];
            const editorState = this.getEditorPart(EditorLayoutDefaultID);
            this.setEditorPart({ ...editorState, children: [{ id: targetGroup.id, __typename: CacheTypenames.EditorGroup }] });
        }
        return {
            group,
            input: editor,
            options: options,
            minimumHeight: 0,
            maximumHeight: 0,
            minimumWidth: 0,
            maximumWidth: 0,
        }
    }
    //#region createInput()

    createInput(input: IEditorInputWithOptions | IEditorInput | IResourceEditor): IEditorInput {

        // Side by Side Support
        const resourceSideBySideInput = <IResourceSideBySide_Input>input;
        if (resourceSideBySideInput.masterResource && resourceSideBySideInput.detailResource) {
            const masterInput = this.createInput({ resource: resourceSideBySideInput.masterResource, forceFile: resourceSideBySideInput.forceFile });
            const detailInput = this.createInput({ resource: resourceSideBySideInput.detailResource, forceFile: resourceSideBySideInput.forceFile });
            const label = resourceSideBySideInput.label || masterInput.label || localize('sideBySideLabels', "{0} - {1}", this.toDiffLabel(masterInput), this.toDiffLabel(detailInput));

            return {
                ...this.getAdditionalDetails(resourceSideBySideInput.options!.pinned, (input as IEditorInput).preferredEditorId),
                ...generateSideBySideEditorInput(
                    label,
                    typeof resourceSideBySideInput.description === 'string' ? resourceSideBySideInput.description : masterInput.description,
                    detailInput,
                    masterInput,
                ),
                resource: resourcePath(resourceSideBySideInput.masterResource),
            };
        }

        // Diff Editor Support
        const resourceDiffInput = <IResourceDiff_Input>input;
        if (resourceDiffInput.leftResource && resourceDiffInput.rightResource) {
            const leftInput = this.createInput({ resource: resourceDiffInput.leftResource, forceFile: resourceDiffInput.forceFile });
            const rightInput = this.createInput({ resource: resourceDiffInput.rightResource, forceFile: resourceDiffInput.forceFile });
            const label = resourceDiffInput.label || localize('compareLabels', "{0} ↔ {1}", this.toDiffLabel(leftInput), this.toDiffLabel(rightInput));

            return {
                ...this.getAdditionalDetails(resourceDiffInput.options.pinned, (input as IEditorInput).preferredEditorId),
                ...generateDiffEditorInput(
                    label,
                    withUndefinedAsNull(resourceDiffInput.description),
                    leftInput,
                    rightInput),
                fileReference: rightInput.fileReference,
                resource: resourcePath(resourceDiffInput.leftResource),
            };
        }

        // Untitled file support
        const untitledInput = <IUntitledResource_Input>input;
        if (untitledInput.forceUntitled || !untitledInput.resource || (untitledInput.resource && untitledInput.resource.scheme === Schemas.untitled)) {

            //@sri convert to tab
            const undoFileStat = this.untitledEditorService
                .createOrGet(untitledInput.resource as any, untitledInput.mode as any, untitledInput.contents as any, untitledInput.encoding as any);
            const result = {
                __typename: CacheTypenames.UntitledEditorInput,
                typeId: EDITOR_INPUT_ID.RESOURCE_EDITOR_INPUT,
                label: undoFileStat.name,
                resource: undoFileStat.resource.toString(),
                fileReference: undoFileStat as any,
                mode: undoFileStat.preferredMode || null,
                enconding: undoFileStat.preferredEncoding || undoFileStat.encoding, 
            } as IUntitledEditorInput;
            return {
                ...this.getAdditionalDetails(true, (input as IEditorInput).preferredEditorId),
                ...result,
            }
        }

        // Resource Editor Support
        const resourceInput = <IResourceInput &  {[key: string]: any}>input;
        const { resource: resResource, label: resLable, description: resDescription, encoding: resEncoding, mode: resMode, forceFile: resForcefile, ...rest } = resourceInput;
        const resource = URI.revive(resResource);
        if (resource instanceof URI) {
            let label = resLable;
            if (!label && resResource.scheme !== Schemas.data) {
                label = basename(resResource); // derive the label from the path (but not for data URIs)
            }

            return this.createOrGet(resource, label, resDescription, resEncoding, resMode, resForcefile, rest) as IEditorInput;
        }

        throw new Error('Unknown input type');
    }

    private createOrGet(resource: URI, label: string | undefined, description: string | undefined, encoding: string | undefined, mode: string | undefined, forceFile: boolean | undefined, additonalProps: object | undefined) {

        const input: IIEditorInput = {
            ...this.getAdditionalDetails(false), //by default set false to isPinned
            label: label ? label : '',
            description,
            // preferredMode: mode,
            // preferredEncoding: fileStat.encoding ? fileStat.encoding : undefined,
        }
        let result: IEditorInput;
        const registry = Registry.as<IEditorInputFactoryRegistry>(ClientExtensions.EditorInputFactories);
        
        // File
        // dynamically get the editorinput based on the resgistryy
        if (forceFile || this.fileService.canHandleResource(resource)) {
            const fileFactory = registry.getFileInputFactory();
            const fileStat = fileFactory.createFileInput(resource, encoding, mode, this.container);
            input.fileReference = fileStat as any;
        }

        if (registry.hasEditorInput(resource)) {
            try {
                result = Object.assign(input, registry.getEditorInput(resource, label, description, mode, this.container, additonalProps));
            } catch(err) {
                console.warn('Skipping EditorInputFactoryRegistry due to an error');
                console.warn(`error skipped: ${err.message}`);
            }
        }

        // Data URI
        else if (resource.scheme === Schemas.data) {
            const metadata = DataUri.parseMetaData(resource);
            let size: number;
            if (metadata.has(DataUri.META_DATA_SIZE)) {
                size = Number(metadata.get(DataUri.META_DATA_SIZE));
            }
            const mime = metadata.get(DataUri.META_DATA_MIME)!;
            result = Object.assign<IIEditorInput, IDataUriEditorInput>(input, {
                __typename: CacheTypenames.DataUriEditorInput,
                typeId: EDITOR_INPUT_ID.DATA_URI_EDITOR_INPUT,
                resource: resource.toString(),
                size,
                mime,
            })
        }

        else {
            result = Object.assign<IIEditorInput, IResourceEditorInput>(input, {
                __typename: CacheTypenames.ResourceEditorInput,
                typeId: EDITOR_INPUT_ID.RESOURCE_EDITOR_INPUT,
                resource: resource.toString(),
                label: label,
                preferredMode: mode || null,
                mode: mode || null,
            })
        }
        // Resource
        return result;
    }

    private getAdditionalDetails(pinned: boolean, preferredEditorId = null): IIEditorInputWithGroupFields {

        let newContent: IIEditorInputWithGroupFields & { preferredEditorId: string } = {
            isPinned: pinned || false,
            closable: true,
            preview: false,
            isActive: false,
            preferredEditorId,
        };
        return newContent;
    }

    private addIds(groupId: number, resource: string | URI, label: string, __typename) {
        const id = this.utility.getCacheKey({
            editorId: groupId,
            resource: resourcePath(resource),
            label,
            __typename,
        });
        return {
            editorId: groupId, // need to rename to `groupId`
            id,
        }
    }

    // TODO
    private getPreferredEditorId(forceOpenAsBinary): string {
		return forceOpenAsBinary ? BINARY_FILE_EDITOR_ID : TEXT_FILE_EDITOR_ID;
	}

    public abstract getFileDetails(resource: string | URI): IFileStatWithMetadata;

    private findTargetGroup(input: IEditorInput, options?: IEditorOptions, group?: IEditorGroup | GroupIdentifier | SIDE_GROUP_TYPE | ACTIVE_GROUP_TYPE): IEditorGroup {
        let targetGroup: IEditorGroup | undefined;

        // Group: Instance of Group
        if (group && typeof group !== 'number') {
            return group;
        }

        // Group: Side by Side
        if (group === SIDE_GROUP) {
            targetGroup = this.findSideBySideGroup();
        }

        // Group: Specific Group
        else if (typeof group === 'number' && group >= 0) {
            targetGroup = this.editorGroupService.getGroup(group);
        }

        // Group: Unspecified without a specific index to open
        else if (!options || typeof options.index !== 'number') {
            const groupsByLastActive = this.editorGroupService.getGroups(GroupsOrder.MOST_RECENTLY_ACTIVE);


            // Respect option to reveal an editor if it is already visible in any group
            if (options && options.revealIfVisible) {
                for (const group of groupsByLastActive) {
                    //@sri similar to vscode
                    const activeTab = group.tabs.find(t => t.isActive);
                    if (input.resource === activeTab.resource){
                        targetGroup = group;
                        break;
                    }
                }
            }

            // Respect option to reveal an editor if it is open (not necessarily visible)
            if ((options && options.revealIfOpened) || (this.configurationService.getValue<boolean>('workbench.editor.revealIfOpen')
                && !_.isEmpty(this.configurationService.getValue('workbench.editor.revealIfOpen')))) {
                for (const group of groupsByLastActive) {
                    const tabs = group.tabs.map(t => t.resource);
                    if(tabs.includes(input.resource)){
                        targetGroup = group;
                        break;
                    }
                }
            }
        }

        // Fallback to active group if target not valid
        if (!targetGroup) {
            targetGroup = this.editorGroupService.activeGroup || { id: DEFAULT_GROUP_ID, tabs: [] };
        }

        return targetGroup;
    }

    private findSideBySideGroup(): IEditorGroup {
        const direction = preferredSideBySideGroupDirection(this.configurationService);

        let neighbourGroup = this.editorGroupService.findGroup({ direction });
        if (!neighbourGroup) {
            neighbourGroup = this.editorGroupService.addGroup(this.editorGroupService.activeGroup, direction);
        }

        return neighbourGroup;
    }

    private toOptions(options?: IEditorOptions): EditorOptions {
        if (!options || options instanceof EditorOptions) {
            return options as EditorOptions;
        }

        const textOptions: ITextEditorOptions = options;
        if (!!textOptions.selection) {
            return TextEditorOptions.create(options);
        }

        return EditorOptions.create(options);
    }

    private toDiffLabel(input: IEditorInput): string | null {
        const res = input.resource;
        if (!res) {
            return null;
        }
        const resource = URI.parse(res);

        // Do not try to extract any paths from simple untitled editors
        if (resource.scheme === Schemas.untitled && !this.untitledEditorService.hasAssociatedFilePath(resource)) {
            return input.fileReference.name;
        }

        // @sri disable `labelService` until it is tested
        // Otherwise: for diff labels prefer to see the path as part of the label
        // return this.labelService.getUriLabel(res, { relative: true });
        input.label;
    }


    // public removeEditor(group: IEditorGroup | GroupIdentifier) {
    //     const groups = this.groups;

    //     let editorId = action.content.editorId || parts.getActiveGroup(groups).id;
    //     let editorOpenedFiles = EditorService.getTabs(editorId);

    //     const index = editorHelpers.indexOf(action.content, editorOpenedFiles);
    //     // @sri custom change assigning false ({focusRecentEditorAfterClose:false}) because isActive is not set in previous file
    //     editorOpenedFiles = editorHelpers.closeEditor(editorOpenedFiles[index], editorOpenedFiles, { focusRecentEditorAfterClose: false });

    //     if (editorOpenedFiles.length === 0) { // if all the editors in the group closed then close editor group
    //         groups = parts.removeGroup(editorId, groups);
    //         // as per https://stackoverflow.com/questions/48596265/deleting-apollo-client-cache-for-a-given-query-and-every-set-of-variables
    //         // we need remove from cache manually
    //         const key = `${CacheTypenames.EditorGroup}:${editorId}`;
    //         EditorService.deleteCacheByKey(key);
    //         // delete openedFiles[editorId]; // delete empty records
    //     } else {
    //         this.setTabs(editorId, editorOpenedFiles);
    //     }
    // }

    protected abstract setTabsWithFiles(editorId: number, tabs): Boolean;

    protected abstract setTabs(editorId: number, tabs): Boolean;

    protected abstract getEditors(editorId: number): IEditorInput[];

    public closeEditor(editor: IEditorInput, layoutId: string) {
        const editorId = editor.editorId || this.editorGroupService.activeGroup.id;
        let group = this.editorGroupService.getGroup(editorId);
        const editorOpenedFiles = groupView.closeInActiveEditor(editor, group.tabs.slice(0));

        if (editorOpenedFiles.length === 0) {
            let groups = this.editorGroupService.removeGroup(editorId).slice(0);
            const activeGroup = groups.filter((group) => group.isActive);
            if (!activeGroup.length) {
               groups[groups.length - 1] = this.editorGroupService.activateGroup(groups[groups.length - 1].id);
            }
            this.editorGroupService.setGroups(groups);
            let editorPart =  this.getEditorPart(layoutId);
            const children = editorPart && editorPart.children.slice(0);
            const groupIndex = _.findIndex(children, (group: IEditorGroup) => group.id === editorId);
            children.splice(groupIndex, 1);
            editorPart = { ...editorPart, children };
            this.setEditorPart(editorPart);
            return true;
        }
        this.setTabs(editorId, editorOpenedFiles);
    }

    public pinEditor(editor: IEditorInput) {
        if (editor.isPinned) {
            return;
        }
        let tabs = this.getEditors(editor.editorId);
        const index = tabs.findIndex((tab) => tab.id === editor.id);
        tabs[index] = { ...tabs[index], isPinned: true }
        this.setTabs(editor.editorId, tabs);
    }
}




